// One row of the agent conversation: a message's thinking block, its text
// bubble, its tool chain, the plugin-rendered widgets hanging off that chain,
// its inline artifact previews, and — where the reader forked a published app —
// the divider that follows it.
//
// Extracted from AgentChat's displayMessages.map so it can be memoised. A long
// session is hundreds of rows with no virtualisation, and every change to the
// chat container (the width toggle, a side panel sliding) used to re-create all
// of them, `sx` objects included. Wrapped in React.memo the row re-renders only
// when its OWN inputs change, so a container change costs nothing per message.
//
// That memo only holds while every prop is a primitive, the message object, or
// a stable reference: AgentChat computes the per-message values (its artifact
// maps, the tool-call maps, the plugin fold) in useMemo and stabilises every
// callback with useCallback. One inline lambda in the parent defeats the memo
// for every row at once. Callbacks created INSIDE the row are free — they are
// not props.
//
// Product-agnostic, like the rest of frontend/src/agent: product content arrives
// through the RenderPlugin seam (plugins.ts) and host callbacks.

import React from 'react'
import { Box, Typography, Chip, Divider } from '@mui/material'
import type {
  AgentMessage,
  ArtifactInfo,
  AskUserQuestionInfo,
  CreatedDashboardInfo,
  RefreshedSkill,
  RenderedChartInfo,
  RenderedTableInfo,
} from '../types.js'
import AgentMarkdown from './AgentMarkdown.js'
import ToolCallGroup, { tryParseImageOutput, isImageToolCall } from './ToolCallGroup.js'
import InlineArtifactPreview from './InlineArtifactPreview.js'
import AskUserCard from './AskUserCard.js'
import ThinkingBlock from './ThinkingBlock.js'
import { CARD_RADIUS, CARD_SX, INNER_RADIUS } from './surface.js'
import type { RenderPlugin, InlineCitationRendererFactory } from '../plugins.js'
import type { DeckAsk } from '../presentationFrame.js'

/** "Now using <name> saved <time>", the time in the reader's time zone. */
export function skillRefreshedLine(sk: RefreshedSkill): string {
  if (!sk.savedAt) return `Now using ${sk.name}`
  const saved = new Date(sk.savedAt * 1000).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
  return `Now using ${sk.name} saved ${saved}`
}

export interface MessageRowProps {
  /** The message this row draws. Its identity is what the memo turns on. */
  message: AgentMessage
  /** This message's thinking block is the one currently being written. */
  activelyThinking: boolean
  /** Draw the "forked from published app" divider below this row. */
  showForkDivider: boolean
  /** Artifacts auto-registered by this message, or undefined for none. */
  autoArtifacts?: ArtifactInfo[]
  /** Artifacts this message registered through a register_artifact tool call. */
  toolArtifacts?: ArtifactInfo[]
  sessionId: string
  apiBaseUrl: string
  authHeader?: string
  isStreaming: boolean
  /** Stable. Opens an artifact in the preview. */
  onOpenPreview: (artifact: ArtifactInfo) => void
  /** Stable. Publishes a webapp artifact (host feature; omitted hides the action). */
  onPublishWebapp?: (sessionId: string, artifact: ArtifactInfo) => void
  /** Stable. Edits a story board in place (#1416; host feature; omitted hides the action). */
  onEditStory?: (sessionId: string, artifact: ArtifactInfo) => void
  /** Stable. A presentation's Ask was pressed (host feature; omitted, the page draws no Ask). */
  onAskFromPage?: (sessionId: string, artifact: ArtifactInfo, ask: DeckAsk) => void
  /** Why Ask is closed here; undefined, it works. */
  askFromPageRefusal?: string
  /** Stable. Records the reader's answer to an ask_user card. */
  onAnswerQuestion: (toolCallId: string, value: string) => void
  /** Stable. Sends the answer on as a message. */
  onSendMessage: (content: string, model?: string, attachmentIds?: string[]) => void
  /** Stable. Set, a card pick goes to the host (the composer) and nothing is sent. */
  onPickAnswer?: (toolCallId: string, value: string) => void
  /** Stable. Host hook for inline citation markers in assistant markdown. */
  renderInlineCitations?: InlineCitationRendererFactory
  /** Stable. Registered render plugins. */
  plugins: RenderPlugin[]
  /** Stable. Plugin state, folded per plugin index then per tool call id. */
  byPlugin: Map<number, Map<string, unknown>>
  /** Stable. Tables/charts/questions/dashboards keyed by tool call id. */
  toolCallTables: Map<string, RenderedTableInfo[]>
  toolCallCharts: Map<string, RenderedChartInfo[]>
  toolCallQuestions: Map<string, AskUserQuestionInfo>
  toolCallDashboards: Map<string, CreatedDashboardInfo>
}

function MessageRow({
  message,
  activelyThinking,
  showForkDivider,
  autoArtifacts,
  toolArtifacts,
  sessionId,
  apiBaseUrl,
  authHeader,
  isStreaming,
  onOpenPreview,
  onPublishWebapp,
  onEditStory,
  onAskFromPage,
  askFromPageRefusal,
  onAnswerQuestion,
  onSendMessage,
  onPickAnswer,
  renderInlineCitations,
  plugins,
  byPlugin,
  toolCallTables,
  toolCallCharts,
  toolCallQuestions,
  toolCallDashboards,
}: MessageRowProps) {
  // Image tool results, parsed once per change to this message's tool calls.
  // Each is a JSON.parse of the whole output (a screenshot is ~630KB); it ran
  // twice per render of the row. Above the early return: hooks run in order.
  const imageResults = React.useMemo(() => {
    const out: { tc: NonNullable<AgentMessage['toolCalls']>[number]; img: { base64: string; mimeType: string } }[] = []
    for (const tc of message.toolCalls ?? []) {
      if (!tc.output || !isImageToolCall(tc)) continue
      const img = tryParseImageOutput(tc.output)
      if (img) out.push({ tc, img })
    }
    return out
  }, [message.toolCalls])

  const hasContent = message.role === 'user' || !!message.content.trim()
  const hasThinking = !!message.thinking
  const hasTools = !!message.toolCalls && message.toolCalls.length > 0
  const hasArtifacts = !!autoArtifacts || !!toolArtifacts
  if (!hasContent && !hasThinking && !hasTools && !hasArtifacts && !message.stopped) return null

  // Plugin slots for one tool call. Only renders when the plugin accumulated
  // state for this specific toolCallId — no event, nothing to draw.
  const renderPluginSlots = (toolCallId: string): React.ReactNode => {
    if (plugins.length === 0) return null
    const slots: React.ReactNode[] = []
    for (let i = 0; i < plugins.length; i++) {
      const plugin = plugins[i]
      const state = byPlugin.get(i)?.get(toolCallId)
      if (state !== undefined) {
        const node = plugin.render({ state, toolCallId, sessionId })
        if (node) slots.push(<React.Fragment key={i}>{node}</React.Fragment>)
      }
    }
    // The tool call id is the scroll target of the Notebook's "Show in chat" (#1869).
    return slots.length > 0 ? <div data-tool-call-id={toolCallId}>{slots}</div> : null
  }

  return (
    <>
      <Box
        data-role={message.role}
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
          // Long sessions (hundreds of messages, no virtualisation):
          // let the browser skip layout and paint for messages that
          // are off screen, so a width change costs only the visible
          // ones. The intrinsic size keeps the scrollbar honest.
          contentVisibility: 'auto',
          containIntrinsicSize: 'auto 120px',
        }}
      >
        {/* Thinking block (extracted component) */}
        {message.thinking && (
          <ThinkingBlock
            content={message.thinking}
            isActivelyThinking={activelyThinking}
          />
        )}
        {/* Hide empty assistant bubbles during streaming */}
        {hasContent && (
          <Box
            data-testid="message-bubble"
            // On the bubble as well as the row, so one selector
            // ([data-testid="message-bubble"][data-role="user"]) finds the
            // reader's turn in every surface that draws rows (SidePaneChat).
            data-role={message.role}
            sx={{
              maxWidth: '80%',
              wordBreak: 'break-word',
              fontSize: '0.8125rem',
              lineHeight: 1.6,
              ...(message.role === 'user' ? {
                // The reader's turn is a navy card: the same corner as every
                // other card in the column, the header's own navy.
                p: '10px 16px',
                borderRadius: CARD_RADIUS,
                backgroundColor: 'primary.main',
                color: 'white',
                whiteSpace: 'pre-wrap',
                // Waiting for the running turn to finish: the same card,
                // quieter, so it reads as not yet sent.
                ...(message.queued ? { opacity: 0.6 } : {}),
              } : {
                // The assistant's prose sits straight on the column — no box,
                // no rail. Side and the navy card are what tell the two apart,
                // and a long answer is not a page of white boxes.
                p: '4px 0',
                color: 'text.primary',
              }),
            }}
          >
            {message.role === 'assistant' ? (
              <AgentMarkdown content={message.content} renderInlineCitations={renderInlineCitations} />
            ) : (
              message.content
            )}
          </Box>
        )}
        {message.queued && (
          <Typography
            data-testid="message-queued-caption"
            sx={{ mt: 0.5, fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}
          >
            Queued: sends when the current turn finishes
          </Typography>
        )}
        {/* Auto-registered artifact previews */}
        {autoArtifacts?.map((artifact, i) => (
          <Box key={`auto-artifact-${message.id}-${i}`} sx={{ maxWidth: artifact.artifactType === 'webapp' ? '100%' : '80%', ...(artifact.artifactType === 'webapp' ? { alignSelf: 'stretch' } : {}), mt: 1 }}>
            <InlineArtifactPreview
              artifact={artifact}
              sessionId={sessionId}
              onOpenPreview={onOpenPreview}
              apiBaseUrl={apiBaseUrl}
              authHeader={authHeader}
              onPublishWebapp={onPublishWebapp}
              onEditStory={onEditStory}
              onAskFromPage={onAskFromPage}
              askFromPageRefusal={askFromPageRefusal}
            />
          </Box>
        ))}
        {/* Tool calls */}
        {hasTools && (
          <Box data-testid="tool-call-block" sx={{ maxWidth: '80%', mt: hasContent ? 1 : 0 }}>
            <ToolCallGroup toolCalls={message.toolCalls!} />
          </Box>
        )}
        {/* Plugin-rendered content (tables, charts, dashboards) and the
            per-tool cards get the FULL column width, not the 80% the
            tool chain and text bubbles use: a chart or a crosstab in a
            four-fifths column is unreadable (Adam, 8 Sep). */}
        {hasTools && (
          <Box data-testid="tool-plugin-slots" sx={{ width: '100%', alignSelf: 'stretch', minWidth: 0 }}>
            {/* Inline tables/charts from renderedTables/Charts — dispatched through plugins when registered */}
            {message.toolCalls!.map(tc => (
              <React.Fragment key={tc.id}>
                {/* Render plugin slots (Platinum table/chart/dashboard widgets register here) */}
                {renderPluginSlots(tc.id)}
                {/* Fallback: show raw table/chart state for hosts without plugins.
                    When plugins are registered (e.g. Platinum's Carbon widgets) the host
                    owns rendering via renderPluginSlots above, so suppress these debug
                    cards — otherwise a stray "📈 Chart: X" leaks in next to the widget. */}
                {plugins.length === 0 && (toolCallTables.get(tc.id) || []).map(table => (
                  <Box key={table.id} sx={{ ...CARD_SX, mt: 1, p: 1.5, fontSize: 12, color: 'text.secondary' }}>
                    📊 Table: {table.title || table.id}
                  </Box>
                ))}
                {plugins.length === 0 && (toolCallCharts.get(tc.id) || []).map(chart => (
                  <Box key={chart.id} sx={{ ...CARD_SX, mt: 1, p: 1.5, fontSize: 12, color: 'text.secondary' }}>
                    📈 Chart: {chart.title || chart.id}
                  </Box>
                ))}
                {toolCallQuestions.has(tc.id) && (
                  <AskUserCard
                    question={toolCallQuestions.get(tc.id)!}
                    onAnswer={(value) => {
                      onAnswerQuestion(tc.id, value)
                      onSendMessage(value)
                    }}
                    onPick={onPickAnswer ? (value) => onPickAnswer(tc.id, value) : undefined}
                    disabled={isStreaming || toolCallQuestions.get(tc.id)!.answered}
                  />
                )}
                {plugins.length === 0 && toolCallDashboards.has(tc.id) && (
                  <Box sx={{ ...CARD_SX, mt: 1, p: 1.5, fontSize: 12, color: 'text.secondary' }}>
                    🗂️ Dashboard created
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
        )}
        {/* View image tool results */}
        {hasTools && imageResults.map(({ tc, img: imgData }) => {
          const filePath = (tc.input?.file_path as string) || 'image.png'
          const fileName = filePath.split('/').pop() || 'Image'
          const dataUrl = `data:${imgData.mimeType};base64,${imgData.base64}`
          const syntheticArtifact: ArtifactInfo = {
            filePath,
            fileName,
            mimeType: imgData.mimeType,
            label: fileName,
            artifactType: 'image',
            source: 'auto',
            status: 'live',
            downloadUrl: dataUrl,
          }
          return (
            <Box key={`view-image-${tc.id}`} sx={{ maxWidth: '80%', mt: 1 }}>
              <InlineArtifactPreview
                artifact={syntheticArtifact}
                sessionId={sessionId}
                dataUrl={dataUrl}
                onOpenPreview={onOpenPreview}
                apiBaseUrl={apiBaseUrl}
                authHeader={authHeader}
              />
            </Box>
          )
        })}
        {/* Inline artifact previews from register_artifact tool calls */}
        {toolArtifacts?.map((artifact, i) => (
          <Box key={`tool-artifact-${message.id}-${i}`} sx={{ maxWidth: artifact.artifactType === 'webapp' ? '100%' : '80%', ...(artifact.artifactType === 'webapp' ? { alignSelf: 'stretch' } : {}), mt: 1 }}>
            <InlineArtifactPreview
              artifact={artifact}
              sessionId={sessionId}
              onOpenPreview={onOpenPreview}
              apiBaseUrl={apiBaseUrl}
              authHeader={authHeader}
              onPublishWebapp={onPublishWebapp}
              onEditStory={onEditStory}
              onAskFromPage={onAskFromPage}
              askFromPageRefusal={askFromPageRefusal}
            />
          </Box>
        ))}
        {/* The turn was stopped: the partial output stays, captioned
            so the reader knows why it ends where it does (#1295). A turn
            whose model call failed before the agent could answer (#1575)
            gets the same caption treatment, with the reason instead of
            "Stopped"/"Interrupted" — no separate red alert banner. */}
        {message.stopped && (
          <Typography
            data-testid={message.failed ? 'message-failed-caption' : 'message-stopped-caption'}
            sx={{ mt: 0.5, fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}
          >
            {message.failed ? `Failed — ${message.failed}` : message.interrupted ? 'Interrupted' : 'Stopped'}
          </Typography>
        )}
      </Box>
      {/* A sentence from the host about the turn this message started (a
          chat started in a new working folder). Plain and quiet: it explains,
          it does not alarm. */}
      {message.notice && (
        <Typography
          role="note"
          data-testid="message-notice"
          sx={{ my: 1.5, mx: 'auto', maxWidth: 560, fontSize: 12, lineHeight: 1.5, color: 'text.secondary', textAlign: 'center' }}
        >
          {message.notice}
        </Typography>
      )}
      {message.skillsRefreshed?.map(sk => (
        <Typography
          key={sk.name}
          role="note"
          data-testid="message-skill-refreshed"
          sx={{ my: 1, mx: 'auto', maxWidth: 560, fontSize: 12, lineHeight: 1.5, color: 'text.secondary', textAlign: 'center' }}
        >
          {skillRefreshedLine(sk)}
        </Typography>
      ))}
      {showForkDivider && (
        <Divider sx={{ my: 2, borderStyle: 'dashed', borderColor: '#d1d5db' }}>
          <Chip
            label="Forked from published app — new messages below"
            size="small"
            sx={{ fontSize: 11, color: '#6b7280', backgroundColor: '#f9fafb', fontWeight: 500, borderRadius: INNER_RADIUS }}
          />
        </Divider>
      )}
    </>
  )
}

export default React.memo(MessageRow)
